import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import PublicIcon from "@mui/icons-material/Public";
import { useEffect, useState } from "react";
import axios from "axios";
import { hostname, parseError } from "../../utils";
import { useEmployeeTerminology } from "../../utils/terminology";

interface ExternalCompanyUsage {
  timesAdded: number;
  totalChats: number;
  totalMessages: number;
}

interface LeaderboardEntry {
  employeeId: string;
  name: string;
  picture?: string;
  chatCount: number;
  messageCount: number;
  isPublicEmployee: boolean;
  externalCompanyUsage?: ExternalCompanyUsage | null;
}

interface LeaderboardData {
  leaderboard: LeaderboardEntry[];
}

export default function EmployeeLeaderboard({
  companyId,
  getToken,
  eventListener,
}: {
  companyId: string;
  getToken: () => Promise<string>;
  eventListener: (event: any) => void;
}) {
  const { employee, employees } = useEmployeeTerminology();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<LeaderboardData | null>(null);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/companies/${companyId}/employees/leaderboard`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setData(response.data);
    } catch (error) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchLeaderboard();
    }
  }, [companyId]);

  const getAvatarText = (entry: LeaderboardEntry) => {
    if (entry.name) {
      const names = entry.name.trim().split(" ");
      if (names.length >= 2) {
        return names[0][0] + names[names.length - 1][0];
      }
      return names[0][0];
    }
    return "?";
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  const getRankBadge = (rank: number) => {
    if (rank === 1) return "🥇";
    if (rank === 2) return "🥈";
    if (rank === 3) return "🥉";
    return `#${rank}`;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader
          avatar={<PersonIcon />}
          title={`${employees} Leaderboard`}
          subheader={`Loading top ${employees.toLowerCase()}...`}
        />
        <CardContent>
          <LinearProgress />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.leaderboard.length === 0) {
    return (
      <Card>
        <CardHeader
          avatar={<PersonIcon />}
          title={`${employees} Leaderboard`}
          subheader={`Top 10 ${employees.toLowerCase()} by usage`}
        />
        <CardContent>
          <Typography variant="body2" color="text.secondary" align="center">
            No usage data available yet
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Check if any employee has external usage to show those columns
  const hasExternalUsage = data.leaderboard.some(
    (entry) => entry.externalCompanyUsage
  );

  return (
    <Card>
      <CardHeader
        avatar={<PersonIcon />}
        title={`${employees} Leaderboard`}
        subheader={`Top 10 ${employees.toLowerCase()} in this company (last 30 days)`}
      />
      <CardContent sx={{ p: 0 }}>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 60 }}>Rank</TableCell>
                <TableCell>{employee}</TableCell>
                <TableCell align="right">Chats</TableCell>
                <TableCell align="right">Messages</TableCell>
                {hasExternalUsage && (
                  <>
                    <TableCell align="right">Times Added</TableCell>
                    <TableCell align="right">Ext. Chats</TableCell>
                    <TableCell align="right">Ext. Messages</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.leaderboard.map((entry, index) => (
                <TableRow
                  key={entry.employeeId}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    backgroundColor: index < 3 ? "action.hover" : "inherit",
                  }}
                >
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: index < 3 ? "bold" : "normal",
                        fontSize: index === 0 ? "1.2rem" : "1rem",
                      }}
                    >
                      {getRankBadge(index + 1)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar
                        src={entry.picture}
                        alt={entry.name}
                        sx={{ width: 32, height: 32 }}
                      >
                        {getAvatarText(entry)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {entry.name}
                        </Typography>
                        {entry.isPublicEmployee && (
                          <Chip
                            icon={<PublicIcon />}
                            label="Public"
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ height: 18, fontSize: "0.7rem" }}
                          />
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">
                      {formatNumber(entry.chatCount)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">
                      {formatNumber(entry.messageCount)}
                    </Typography>
                  </TableCell>
                  {hasExternalUsage && (
                    <>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {entry.externalCompanyUsage
                            ? entry.externalCompanyUsage.timesAdded
                            : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {entry.externalCompanyUsage
                            ? formatNumber(entry.externalCompanyUsage.totalChats)
                            : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {entry.externalCompanyUsage
                            ? formatNumber(
                                entry.externalCompanyUsage.totalMessages
                              )
                            : "-"}
                        </Typography>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}
